<!-- src/views/admin/DeviceDetails.vue -->
<template>
  <admin-layout :title="device ? device.name : 'Chargement...'">

    <!-- Bannière perte WebSocket -->
    <div v-if="!wsConnected" class="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium">
      <div class="flex items-center gap-2">
        <span class="inline-block w-2 h-2 rounded-full bg-yellow-500 animate-pulse flex-shrink-0"></span>
        Connexion temps réel perdue — les données peuvent être obsolètes. Reconnexion en cours...
      </div>
      <button @click="reloadDevice" class="flex-shrink-0 px-3 py-1 rounded border border-yellow-400 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-xs font-semibold transition-colors">
        Actualiser
      </button>
    </div>

    <!--
      Alarme non vue par le client (B11). Affichée sans bouton : c'est au client de dire qu'il l'a
      vue — un admin qui l'effacerait retirerait l'alerte à quelqu'un qui ne l'a peut-être pas vue
      (le serveur le refuse d'ailleurs, ALARME_VUE_COMPTE).
    -->
    <div v-if="device && device.alarm_unseen" class="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm">
      <span class="inline-block w-2 h-2 mt-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0"></span>
      <div>
        <div class="font-semibold">Alarme non vue par le client</div>
        <div class="mt-0.5">
          Dernière alarme le {{ formatDate(device.alarm_unseen.at) }}<span v-if="device.alarm_unseen.count > 1">
          ({{ device.alarm_unseen.count }} en attente)</span>. Elle reste en rouge chez le client jusqu'à
          son « J'ai vu » ou au désarmement.
        </div>
      </div>
    </div>

    <!--
      Perte de protection, pas avertissement technique. Le firmware a déclaré le capteur filaire
      en panne (> 10 fronts/s) et a SUSPENDU la surveillance de l'entrée : pendant ce temps, une
      fuite d'eau réelle ne remontera pas. Ça se dit en haut de page et ça reste affiché tant que
      le défaut dure — une ligne dans l'historique se serait fait enterrer par la suivante.
    -->
    <div v-if="wiredFault" class="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm">
      <span class="inline-block w-2 h-2 mt-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0"></span>
      <div>
        <div class="font-semibold">Capteur filaire en panne — zone non protégée</div>
        <div class="mt-0.5">
          L'entrée filaire n'est plus surveillée : ses déclenchements ne sont ni transmis ni
          notifiés jusqu'au retour à la normale. Vérifier le câblage et le capteur.
          <span v-if="device && device.wired_fault_since"> Depuis {{ formatDate(device.wired_fault_since) }}.</span>
        </div>
      </div>
    </div>

    <!--
      Armement accepté sur zone filaire ouverte. La carte a armé quand même — décision firmware,
      parce qu'un refus sur un armement par planning ou télécommande ne serait lu par personne et
      laisserait la maison désarmée en silence. Le prix de ce choix, c'est que l'avertissement doit
      VIVRE à l'écran : le système est armé, mais cette zone-là ne protège pas.
    -->
    <div v-if="armZoneOpen" class="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm">
      <span class="inline-block w-2 h-2 mt-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
      <div>
        <div class="font-semibold">Armé avec l'entrée filaire déjà ouverte</div>
        <div class="mt-0.5">
          Le système est armé, mais la zone filaire ne protège pas tant qu'elle reste active.
          Armement demandé par {{ armZoneOpenOrigine }}<span v-if="device && device.arm_zone_open_at">,
          {{ formatDate(device.arm_zone_open_at) }}</span>.
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>

    <div v-else-if="!device" class="text-center py-12 text-gray-500">Dispositif introuvable.</div>

    <div v-else>
      <!-- En-tête état live -->
      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <div class="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h2 class="text-xl font-bold text-gray-900">{{ device.name }}</h2>
            <p class="text-sm text-gray-500">{{ device.location }} &bull; <span class="font-mono">{{ device.ident }}</span></p>
            <p class="text-xs text-gray-400">Client : {{ device.client_name }}</p>
          </div>
          <div class="flex flex-wrap gap-2 items-center">
            <!-- État système -->
            <span :class="`px-3 py-1 rounded-full text-sm font-semibold ${stateBadge}`">{{ stateLabel }}</span>
            <!-- Alimentation -->
            <span :class="`px-3 py-1 rounded-full text-sm font-semibold ${powerBadge}`">{{ powerLabel }}</span>
            <!-- Batterie -->
            <!-- Pas de badge « autonomie / réserve en jours » : décision 24/08, le chiffre ne
                 veut pas dire grand-chose pour l'utilisateur. La mesure fine est le mA·h affiché
                 avec le %, et la conso mesurée (mA·h/jour) vit dans l'onglet Batterie. -->
            <span v-if="device.bat_pct !== null && device.bat_pct !== undefined"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              🔋 {{ device.bat_pct }}%<span v-if="device.cap" class="font-normal text-gray-500"> · {{ device.cap }} mA·h</span>
            </span>
            <!-- Signal GSM -->
            <span v-if="device.gsm_rssi !== null && device.gsm_rssi !== undefined && device.gsm_rssi !== 99"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              📶 {{ device.gsm_rssi }}/31
            </span>
            <!-- Température -->
            <span v-if="device.temp !== null && device.temp !== undefined"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              🌡 {{ device.temp }}°C
            </span>
            <!-- Mode éco (réservé au mode solaire — batterie faible avec panneau solaire) -->
            <span v-if="device.eco_mode && device.solar_mode"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700 animate-pulse">
              🔋 Mode éco
            </span>
            <!-- Fenêtre de maintenance -->
            <span v-if="maintWindowOpen"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700 animate-pulse">
              🔧 Maintenance {{ maintCountdown }}s
            </span>
          </div>
        </div>
        <div v-if="device.last_seen" class="mt-2 text-xs text-gray-400">
          Dernier message : {{ formatDate(device.last_seen) }}
        </div>
      </div>

      <!-- Onglets -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-6 overflow-x-auto">
          <button v-for="tab in tabs" :key="tab.id"
                  @click="activeTab = tab.id"
                  :class="`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`">
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <!-- Onglet : Statut & Configuration -->
      <!--
        Onglet « Statut & Config », aligné sur l'onglet « État » du client.

        Il présentait les mêmes informations que le client mais en moins lisible — grille à
        deux colonnes, libellés en noms de composants (« Sensibilité accéléro », « RF
        (CC1101) »), et surtout des MANQUES par rapport à la vue cliente : ni version de
        firmware — introuvable ailleurs dans toute la vue admin, alors que c'est la première
        chose qu'on regarde pour déboguer — ni état du capteur filaire, ni ce que la carte
        RAPPORTE réellement pour sa radio.

        Le tableau reprend donc celui du client, ligne pour ligne, et y ajoute ce qui n'a de
        sens que pour un administrateur : le firmware, l'écart entre l'intention et ce que la
        carte rapporte, la santé de la puce CC1101, la date de la dernière lecture de
        configuration.

        Rien de propre à l'admin n'a été retiré : la bannière de fenêtre de maintenance, le
        bouton « Lire EEPROM » (get_config), la modification du nom et de la localisation, et
        la suppression du dispositif sont tous conservés.
      -->
      <div v-if="activeTab === 'status'">
        <maintenance-window-alert
          :open="maintWindowOpen"
          :countdown="maintCountdown"
          :loading="cmdLoading"
          @close="sendCmd('close_window')"
        />

        <div class="space-y-6">

          <!-- ══ Ce que la carte rapporte ══════════════════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow overflow-hidden">
            <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-3">
              <div>
                <h3 class="font-semibold text-gray-800">État rapporté par la carte</h3>
                <p class="mt-0.5 text-xs text-gray-500">
                  Ce que la carte dit appliquer, pas ce qu'on lui a demandé. Un réglage envoyé
                  à une carte endormie n'apparaît ici qu'à son réveil.
                </p>
              </div>
              <button @click="sendCmdAndReload('get_config')" :disabled="cmdLoading"
                      class="text-xs text-primary-600 hover:underline whitespace-nowrap">
                Lire l'EEPROM
              </button>
            </div>

            <dl class="divide-y divide-gray-100">
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Identifiant (IMEI)</dt>
                <dd class="font-mono text-gray-900 text-right">{{ device.ident || '—' }}</dd>
              </div>
              <!-- La version de firmware n'était affichée NULLE PART dans la vue admin, alors
                   qu'elle conditionne la moitié des comportements documentés ici (le reboot
                   automatique sur changement de radio, par exemple, dépend du seuil 66356). -->
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Version du firmware</dt>
                <!-- En clair (vX.Y.Z), le code brut à côté : c'est lui qu'on lit dans les journaux
                     et dans les seuils du code (66356…). -->
                <dd class="text-gray-900 text-right">
                  <template v-if="device.fw_ver != null">
                    {{ fwLabel(device.fw_ver) }}
                    <span class="ml-1 font-mono text-xs text-gray-400">{{ device.fw_ver }}</span>
                  </template>
                  <template v-else>—</template>
                </dd>
              </div>
              <!-- Profil batterie paramétré en EEPROM, rapporté au boot. Affiché tel quel : la
                   correspondance profil → cellule vit dans le firmware. Un profil faux se voit
                   à la jauge (≈ 500 mA·h annoncés sur une INR21700-50E, carte …7632, 29/09). -->
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Profil batterie</dt>
                <dd class="text-gray-900 text-right">
                  {{ device.batt_profile != null ? `profil ${device.batt_profile}` : 'non rapporté' }}
                  <span v-if="device.batt_profile == null" class="block text-xs text-gray-400">publié au démarrage de la carte</span>
                </dd>
              </div>
              <!-- Contrôle de la jauge (note fw 2.10.6) : fullcap du 1er daily après boot contre la
                   capacité du profil, et dq bloqué à zéro. Calculé par le serveur. -->
              <div v-if="jauge" class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Jauge batterie</dt>
                <dd class="text-right font-medium"
                    :class="jauge.ton === 'alerte' ? 'text-red-600' : (jauge.ton === 'ok' ? 'text-green-600' : 'text-gray-600')">
                  {{ jauge.texte }}
                  <span class="block text-xs font-normal text-gray-400">{{ jauge.detail }}</span>
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Alimentation <span class="text-gray-400">(auto-détectée)</span></dt>
                <dd class="text-right font-medium"
                    :class="device.solar_mode ? 'text-orange-600' : (device.power_source === 'mains' ? 'text-blue-600' : 'text-gray-700')">
                  {{ powerLabel }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Armement</dt>
                <dd class="text-gray-900 text-right">{{ stateLabel }}</dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Dernier message</dt>
                <dd class="text-gray-900 text-right">{{ device.last_seen ? formatDate(device.last_seen) : '—' }}</dd>
              </div>

              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Position GPS</dt>
                <dd class="text-right font-medium" :class="device.gnss_enabled ? 'text-green-600' : 'text-gray-400'">
                  {{ device.gnss_enabled ? 'activée' : 'désactivée' }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Sensibilité au choc</dt>
                <dd class="text-gray-900 text-right">{{ device.accel_sensitivity || 'very_high' }}</dd>
              </div>

              <!-- La radio en TROIS lignes et non une : l'intention, ce que la carte rapporte,
                   et la santé de la puce. Une carte peut très bien avoir la radio « activée »
                   partout et le CC1101 en défaut — `get_config` rapporte l'intention, jamais
                   la panne. La vue cliente n'en montre qu'une, c'est normal : le client ne
                   peut rien faire de l'écart. -->
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Radio des accessoires <span class="text-gray-400">— demandé</span></dt>
                <dd class="text-right font-medium" :class="device.rf_enabled ? 'text-green-600' : 'text-gray-400'">
                  {{ device.rf_enabled ? 'activée' : 'désactivée' }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Radio des accessoires <span class="text-gray-400">— rapporté</span></dt>
                <dd v-if="rfReported === null" class="text-gray-400 italic text-right">jamais rapporté</dd>
                <dd v-else class="text-right font-medium"
                    :class="rfReported === !!device.rf_enabled
                            ? (rfReported ? 'text-green-600' : 'text-gray-400')
                            : 'text-amber-700'">
                  {{ rfReported ? 'activée' : 'désactivée' }}
                  <span v-if="rfReported !== !!device.rf_enabled" class="block text-xs font-normal">écart avec le demandé</span>
                </dd>
              </div>
              <div v-if="device.rf_fault != null" class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Puce CC1101</dt>
                <dd class="text-right font-medium" :class="device.rf_fault ? 'text-red-600' : 'text-green-600'">
                  {{ device.rf_fault ? 'en défaut' : 'initialisée' }}
                </dd>
              </div>

              <div v-if="device.wired_label" class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— nom</span></dt>
                <dd class="text-gray-900 text-right font-medium">{{ device.wired_label }}</dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— repos</span></dt>
                <dd class="text-gray-900 text-right">
                  {{ device.alarm_nc ? 'NC (circuit ouvert = alarme)' : 'NO (contact fermé = alarme)' }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— rôle</span></dt>
                <dd class="text-right font-medium" :class="wiredRole.h24 ? 'text-amber-600' : 'text-gray-900'">
                  {{ wiredRoleLabel }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— sirène</span></dt>
                <dd class="text-right font-medium" :class="wiredRole.siren ? 'text-green-600' : 'text-gray-400'">
                  {{ wiredRole.siren ? 'déclenchée' : 'silencieuse (alerte tout de même remontée)' }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— remontée d'état</span></dt>
                <dd class="text-right font-medium" :class="device.alarm_report ? 'text-green-600' : 'text-gray-400'">
                  {{ device.alarm_report ? 'activée' : 'désactivée' }}
                </dd>
              </div>
              <div class="px-4 py-3 flex justify-between items-start gap-4 text-sm">
                <dt class="text-gray-500">Entrée filaire <span class="text-gray-400">— état actuel</span></dt>
                <dd class="text-right">
                  <span :class="`px-2 py-0.5 rounded-full text-xs font-medium ${alarmInputBadge}`">{{ alarmInputLabel }}</span>
                  <span v-if="alarmInputNote" class="block mt-0.5 text-xs text-gray-400">{{ alarmInputNote }}</span>
                </dd>
              </div>
              <!-- Le capteur en panne était absent de cette vue alors que la vue cliente
                   l'affiche : c'est une PERTE DE PROTECTION, pas un détail de configuration. -->
              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Capteur filaire</dt>
                <dd class="text-right font-medium" :class="wiredFault ? 'text-red-600' : 'text-gray-900'">
                  {{ wiredFault ? 'EN PANNE — zone non protégée' : 'normal' }}
                </dd>
              </div>

              <div class="px-4 py-3 flex justify-between gap-4 text-sm">
                <dt class="text-gray-500">Dernière lecture de configuration</dt>
                <dd class="text-gray-900 text-right">{{ reportedConfigAt }}</dd>
              </div>
            </dl>
          </div>

          <!-- ══ Écart désiré / rapporté ═══════════════════════════════════════════════ -->
          <!-- Repris de l'onglet Commandes, où il reste affiché : c'est là qu'on agit, mais
               c'est ICI qu'on vient constater l'état d'une carte. Un écart qui persiste après
               un `set_config` acquitté est un refus côté carte, pas un retard de livraison. -->
          <div v-if="configDivergence.length" class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 class="font-semibold text-amber-900 text-sm">La carte n'applique pas la configuration demandée</h3>
            <ul class="mt-2 space-y-1">
              <li v-for="d in configDivergence" :key="d.field" class="text-sm text-amber-900">
                {{ d.label }} — demandé : <strong>{{ fmtCfgValue(d.desired) }}</strong>,
                rapporté par la carte : <strong>{{ fmtCfgValue(d.reported) }}</strong>
              </li>
            </ul>
          </div>

          <!-- ══ Actions — propres à l'administrateur ══════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-gray-800">Actions</h3>
            <p class="mt-1 text-sm text-gray-500">
              Réservées à l'administrateur : le client peut décrire son installation, pas la
              retirer du parc.
            </p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button @click="editDeviceModal = true" class="btn btn-primary text-sm">
                Modifier le nom et la localisation
              </button>
              <button @click="confirmDeleteDevice" class="btn btn-danger text-sm">
                Supprimer le dispositif
              </button>
            </div>
            <p class="mt-2 text-xs text-gray-500">
              La suppression retire la carte du parc et son historique avec elle. Elle ne
              virginise pas la carte : celle-ci continue de publier jusqu'à révocation de son
              jeton.
            </p>
          </div>
        </div>

        <!-- Modal modifier device -->
        <div v-if="editDeviceModal" class="fixed z-10 inset-0 overflow-y-auto">
          <div class="flex items-center justify-center min-h-screen px-4">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="editDeviceModal = false"></div>
            <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 class="text-lg font-medium mb-4">Modifier le dispositif</h3>
              <div class="space-y-3">
                <div>
                  <label class="form-label">Nom</label>
                  <input type="text" v-model="editDevice.name" class="form-control">
                </div>
                <div>
                  <label class="form-label">Localisation</label>
                  <input type="text" v-model="editDevice.location" class="form-control">
                </div>
              </div>
              <div class="mt-4 flex gap-2 justify-end">
                <button @click="editDeviceModal = false" class="btn btn-secondary">Annuler</button>
                <button @click="saveDevice" class="btn btn-primary">Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet : Accessoires RF -->
      <div v-if="activeTab === 'accessories'">
        <maintenance-window-alert
          :open="maintWindowOpen"
          :countdown="maintCountdown"
          :loading="cmdLoading"
          @close="sendCmd('close_window')"
        />
        <!-- Composant partagé avec l'espace client. `technique` ajoute l'adresse radio et le
             code réseau, utiles au dépannage et illisibles pour un client. -->
        <accessories-panel :device-id="deviceId" :accessories="accessories"
                           :technique="true" :tout-retirer="true"
                           @maj="accessories = $event" />
      </div>

      <!-- Onglet : Contacts d'alerte. Composant partagé avec l'espace client : la liste,
           le formulaire et la validation du numéro vivaient ici en double, et la copie
           cliente n'avait jamais reçu de boutons. -->
      <div v-if="activeTab === 'contacts'">
        <contacts-panel :device-id="deviceId" :contacts="contacts" @maj="contacts = $event" />
      </div>

      <!--
        Onglet Commandes, aligné sur la présentation de l'espace client.

        Ce qui a changé, et pourquoi : la grille à deux colonnes mettait côte à côte des blocs
        de hauteurs très différentes — l'œil sautait de l'un à l'autre et l'ordre de lecture
        devenait impossible à deviner. Les titres parlaient en noms de composants (« GPS
        (GNSS) », « Radio RF (CC1101) », « charge_status ») et les explications arrivaient
        SOUS les boutons, en gris pâle, c'est-à-dire après la décision.

        Désormais : une colonne, du geste le plus courant au plus rare, un titre en langage
        ordinaire et une phrase en tête qui dit à quoi sert le bloc. Le nom technique reste,
        en gris à côté du titre — un administrateur qui déboguе a besoin de savoir que
        « Rapports de charge » s'appelle `charge_status` dans les journaux.

        AUCUN contrôle ni avertissement n'a été retiré : plusieurs de ces textes corrigent des
        erreurs constatées sur le terrain (le reboot refusé en silence sur batterie, la
        remontée d'état qui n'empêche pas le tir, le CC1101 en défaut).
      -->
      <div v-if="activeTab === 'commands'">
        <maintenance-window-alert
          :open="maintWindowOpen"
          :countdown="maintCountdown"
          :loading="cmdLoading"
          @close="sendCmd('close_window')"
        />

        <!-- Écart entre la config DEMANDÉE et celle que la carte rapporte appliquer. Tant que les
             deux partageaient les mêmes colonnes, un désaccord était invisible : la valeur
             oscillait en silence à chaque rapport. Il est désormais affiché tel quel. -->
        <div v-if="configDivergence.length" class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h3 class="font-semibold text-amber-900 text-sm">La carte n'applique pas la configuration demandée</h3>
          <ul class="mt-2 space-y-1">
            <li v-for="d in configDivergence" :key="d.field" class="text-sm text-amber-900">
              {{ d.label }} — demandé : <strong>{{ fmtCfgValue(d.desired) }}</strong>,
              rapporté par la carte : <strong>{{ fmtCfgValue(d.reported) }}</strong>
            </li>
          </ul>
          <p class="text-xs text-amber-700 mt-2">
            Dernier rapport de configuration : {{ reportedConfigAt }}.
            Un écart qui persiste après un `set_config` acquitté est un refus côté carte, pas un retard de livraison.
          </p>
        </div>

        <div class="space-y-6">

          <!-- ══ Armement ══════════════════════════════════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-gray-800">Armement</h3>
            <p class="mt-1 text-sm text-gray-500">
              Met le système en surveillance, ou l'en sort. Le planning automatique fait la
              même chose aux horaires définis dans son onglet.
            </p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button @click="sendCmd('arm')" class="btn btn-warning text-sm disabled:opacity-50 disabled:cursor-not-allowed" :disabled="cmdLoading || injoignable">Armer</button>
              <button @click="sendCmd('disarm')" class="btn btn-success text-sm disabled:opacity-50 disabled:cursor-not-allowed" :disabled="cmdLoading || injoignable">Désarmer</button>
            </div>
            <p v-if="injoignable" class="mt-2 text-xs text-amber-700">{{ messageEndormie() }}</p>
          </div>

          <!-- ══ État ══════════════════════════════════════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-gray-800">État de la carte</h3>
            <p class="mt-1 text-sm text-gray-500">
              Demande à la carte de rapporter immédiatement son état et sa configuration, au
              lieu d'attendre son rapport périodique.
            </p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button @click="sendCmd('status')" class="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed" :disabled="cmdLoading || injoignable">
                Demander un rapport d'état
              </button>
              <button @click="sendCmd('list_accessories')" class="btn bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                      :disabled="cmdLoading">
                Interroger la liste EEPROM
              </button>
            </div>
          </div>

          <!-- Jumelage, choc, GPS, entrée filaire, cadences : composants PARTAGÉS avec l'espace
               client (components/device/commands/), pour que les deux vues ne dérivent plus.
               `technique` ajoute les noms firmware et les réglages d'installateur. -->
          <pairing-card :device-id="deviceId" :send="sendCmd" :busy="cmdLoading" :technique="true" :injoignable="injoignable" />
          <accel-card :device="device" :send="sendCmd" :busy="cmdLoading" :technique="true" />
          <gnss-card :device="device" :send="sendCmd" :busy="cmdLoading" :technique="true" />
          <wired-input-card :device="device" :send="sendCmd" :busy="cmdLoading" :technique="true" />

          <!-- ══ Radio ═════════════════════════════════════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-gray-800">
              Radio des accessoires
              <span class="ml-1 text-xs font-normal text-gray-400">CC1101</span>
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              Sans elle, la carte n'entend plus aucun accessoire sans fil. Réservée à
              l'administrateur : rien, côté client, ne dirait que la coupure est volontaire.
            </p>

            <dl class="mt-3 space-y-1">
              <div class="flex items-center justify-between text-sm">
                <dt class="text-gray-500">État désiré</dt>
                <dd :class="device.rf_enabled ? 'font-medium text-green-600' : 'font-medium text-gray-400'">
                  {{ device.rf_enabled ? 'Activée' : 'Désactivée' }}
                </dd>
              </div>
              <div class="flex items-center justify-between text-sm">
                <dt class="text-gray-500">Rapporté par la carte</dt>
                <dd v-if="rfReported === null" class="text-gray-400 italic">jamais rapporté</dd>
                <dd v-else :class="rfReported === !!device.rf_enabled
                        ? (rfReported ? 'font-medium text-green-600' : 'font-medium text-gray-400')
                        : 'font-medium text-amber-700'">
                  {{ rfReported ? 'Activée' : 'Désactivée' }}
                </dd>
              </div>
              <!-- Santé MATÉRIELLE de la puce, distincte des deux lignes ci-dessus qui portent
                   l'intention. Une carte peut très bien avoir la radio « activée » partout et le
                   CC1101 en défaut : `get_config` rapporte l'intention, jamais la panne. -->
              <div v-if="device.rf_fault != null" class="flex items-center justify-between text-sm">
                <dt class="text-gray-500">Puce CC1101</dt>
                <dd :class="device.rf_fault ? 'font-medium text-red-600' : 'font-medium text-green-600'">
                  {{ device.rf_fault ? 'en défaut' : 'initialisée' }}
                </dd>
              </div>
            </dl>

            <p v-if="device.rf_fault" class="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              Le CC1101 n'a pas pu s'initialiser au démarrage : la carte tourne en mode dégradé et
              ne reçoit aucun accessoire radio. Ni un jumelage ni une réactivation ne peuvent
              aboutir dans cet état — c'est un retour matériel.
            </p>

            <div class="mt-3 flex flex-wrap gap-2">
              <button @click="sendCmd('rf_enable')" class="btn btn-primary text-sm" :disabled="cmdLoading || device.rf_enabled">Activer la radio</button>
              <button @click="sendCmd('rf_disable')" class="btn btn-secondary text-sm" :disabled="cmdLoading || !device.rf_enabled">Désactiver la radio</button>
            </div>

            <!-- Depuis v1.3.52 (66356), un changement RÉEL du drapeau déclenche un reboot différé
                 côté carte : l'ancien texte « il faut redémarrer vous-même » y est devenu faux. -->
            <p v-if="rfRebootsItself" class="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <strong>La carte redémarre d'elle-même.</strong> L'accusé de réception arrive d'abord,
              puis la carte disparaît quelques dizaines de secondes, le temps de redémarrer pour appliquer
              le changement. <strong>Ce n'est pas une perte de contact</strong> : ne renvoyez pas la commande.
              N'enchaînez pas non plus un jumelage dans la foulée — le redémarrage couperait la fenêtre.
              Repousser la même valeur ne redémarre rien.
            </p>
            <p v-else class="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <strong>Effet au prochain redémarrage.</strong> Le réglage est enregistré immédiatement dans la carte,
              mais l'initialisation du CC1101 et sa mise en veille sont décidées au démarrage : tant que la
              carte n'a pas redémarré, les accessoires radio continuent de fonctionner. Un appairage est
              refusé dès maintenant, ce n'est pas un échec de la commande.
              <span class="block mt-1">Cette carte est en firmware antérieur à la v1.3.52 : le redémarrage
              est à déclencher à la main ci-dessous.</span>
            </p>
          </div>

          <!-- ══ Cadence des rapports ══════════════════════════════════════════════════ -->
          <report-intervals-cards :device="device" :send="sendCmd" :busy="cmdLoading" :technique="true" />

          <!-- ══ Redémarrage ═══════════════════════════════════════════════════════════ -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-gray-800">Redémarrage</h3>
            <p class="mt-1 text-sm text-gray-500">
              La carte se coupe puis revient en une minute environ. Sans effet destructeur —
              ni la configuration ni les accessoires jumelés ne sont perdus.
            </p>
            <button @click="sendCmd('reboot')" class="btn btn-warning text-sm mt-3" :disabled="cmdLoading">
              Redémarrer la carte
            </button>
            <p class="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
              <strong>Carte sur batterie :</strong> le firmware refuse le redémarrage hors fenêtre de
              maintenance, et sans aucun message d'erreur. Appuyez sur le <strong>bouton PANIC</strong>
              de la carte pour ouvrir une fenêtre de 120 s — la commande mise en file partira alors
              d'elle-même. Sur secteur ou solaire, elle est envoyée immédiatement.
            </p>
          </div>
        </div>

        <!-- Feedback commande -->
        <div v-if="cmdFeedback" :class="`mt-4 p-3 rounded text-sm ${cmdFeedback.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`">
          {{ cmdFeedback.message }}
        </div>

        <p class="mt-4 text-xs text-gray-400">
          Les commandes sont délivrées immédiatement si le device est alimenté (secteur ou solaire) — il reste alors
          abonné en permanence à <code>/cmd</code>. En mode batterie seule, le device dort : les commandes ne sont
          reçues que pendant la fenêtre de maintenance (bouton PANIC → 2 min).
        </p>
      </div>
      <!-- Onglet : Planning -->
      <div v-if="activeTab === 'schedule'">
        <maintenance-window-alert
          :open="maintWindowOpen"
          :countdown="maintCountdown"
          :loading="cmdLoading"
          @close="sendCmd('close_window')"
        />
        <device-schedule :device-id="deviceId" :is-admin="true" :modifiable="true" />
      </div>

      <!-- Onglet : Batterie -->
      <div v-if="activeTab === 'battery'">
        <battery-panel :device-id="deviceId" :reinitialisable="true" />
      </div>

      <!-- Onglet : Position GPS -->
      <div v-if="activeTab === 'gps'">
        <device-map :position="lastGnssPosition" />
      </div>

      <!-- Onglet : Événements -->
      <div v-if="activeTab === 'events'">
        <event-list :events="events" title="Historique des événements"
                    :more-link="{ path: '/admin/events', query: { deviceId: $route.params.deviceId } }" />
      </div>

    </div>
  </admin-layout>
</template>

<script>
import { enParis } from '@/i18n/dates';
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import EventList from '@/components/common/EventList.vue';
import DeviceMap      from '@/components/device/DeviceMap.vue';
import DeviceSchedule from '@/components/device/DeviceSchedule.vue';
import ContactsPanel from '@/components/device/ContactsPanel.vue';
import AccessoriesPanel from '@/components/device/AccessoriesPanel.vue';
import MaintenanceWindowAlert from '@/components/device/MaintenanceWindowAlert.vue';
import BatteryPanel from '@/components/common/BatteryPanel.vue';
import { versionLabel } from '@/utils/fwVersion';
import { carteEndormie, messageEndormie } from '@/utils/carte';
import { libelleJauge } from '@/utils/gauge';
import PairingCard from '@/components/device/commands/PairingCard.vue';
import AccelCard from '@/components/device/commands/AccelCard.vue';
import GnssCard from '@/components/device/commands/GnssCard.vue';
import WiredInputCard from '@/components/device/commands/WiredInputCard.vue';
import ReportIntervalsCards from '@/components/device/commands/ReportIntervalsCards.vue';
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';

export default defineComponent({
  name: 'AdminDeviceDetails',
  components: { AdminLayout, EventList, DeviceMap, DeviceSchedule, ContactsPanel, AccessoriesPanel, MaintenanceWindowAlert, BatteryPanel,
                PairingCard, AccelCard, GnssCard, WiredInputCard, ReportIntervalsCards },

  setup() {
    const store  = useStore();
    const router = useRouter();
    const route  = useRoute();
    const deviceId = parseInt(route.params.deviceId);

    const loading = ref(true);
    const device  = ref(null);
    const accessories = ref([]);
    const contacts = ref([]);
    const events  = ref([]);
    const lastGnssPosition = ref(null);

    const activeTab = ref('status');
    const tabs = [
      { id: 'status',      label: 'Statut & Config' },
      { id: 'accessories', label: 'Accessoires RF' },
      { id: 'contacts',    label: 'Contacts' },
      { id: 'commands',    label: 'Commandes' },
      { id: 'schedule',    label: 'Planning' },
      { id: 'battery',     label: 'Batterie' },
      { id: 'gps',         label: 'Position GPS' },
      { id: 'events',      label: 'Événements' }
    ];


    // Status
    const stateBadge = computed(() => {
      if (!device.value) return 'bg-gray-100 text-gray-500';
      switch (device.value.state) {
        case 'armed':     return 'bg-orange-100 text-orange-700';
        case 'triggered': return 'bg-red-100 text-red-700';
        case 'disarmed':  return 'bg-green-100 text-green-700';
        default:          return 'bg-gray-100 text-gray-500';
      }
    });
    const stateLabel = computed(() => {
      if (!device.value) return '';
      switch (device.value.state) {
        case 'armed':     return 'Armé';
        case 'triggered': return 'Déclenché';
        case 'disarmed':  return 'Désarmé';
        // Aucun message reçu depuis le rattachement : « Inconnu » laissait croire à une panne,
        // alors que la carte n'a simplement pas encore été allumée (ou pas encore parlé).
        default:          return device.value.last_seen ? 'Inconnu' : 'Jamais connectée';
      }
    });
    const powerBadge = computed(() => {
      if (!device.value) return 'bg-gray-100 text-gray-500';
      if (device.value.solar_mode) return 'bg-amber-100 text-amber-700';
      switch (device.value.power_source) {
        case 'mains':    return 'bg-blue-100 text-blue-700';
        case 'battery':  return 'bg-yellow-100 text-yellow-700';
        case 'solar':    return 'bg-amber-100 text-amber-700';
        default:         return 'bg-gray-100 text-gray-500';
      }
    });
    const powerLabel = computed(() => {
      if (!device.value) return '';
      if (device.value.solar_mode) return 'Solaire';
      switch (device.value.power_source) {
        case 'mains':    return 'Secteur';
        case 'battery':  return 'Batterie';
        case 'solar':    return 'Solaire';
        default:         return 'Alimentation inconnue';
      }
    });

    // État temps réel de l'entrée filaire (alarm_input_state, poussé via device:update).
    //
    // 🔴 Ce badge n'a qu'UNE source : l'événement `alarm_input` — lui-même conditionné par
    // `alarm_report`. `config_report` ne porte pas le niveau de l'entrée, aucun autre message non
    // plus. Donc remontée coupée = valeur figée sur la dernière transition connue, parfois
    // vieille de plusieurs jours. Afficher « Au repos » en vert dans ce cas, sur un écran de
    // sécurité, c'est le faux rassurant qu'on a déjà payé sur ce projet : on dit « Inconnu ».
    // La dernière valeur connue reste affichée à côté, en petit — elle a une valeur de
    // diagnostic, pas une valeur d'état courant, et la nuance doit se voir.
    const alarmInputFiable = computed(() => {
      const d = device.value;
      if (!d) return false;
      if (d.alarm_input_state === null || d.alarm_input_state === undefined) return false;
      // L'armement force la remontée le temps de l'armement : la valeur est alors rafraîchie.
      return wiredRole.value.report || d.state === 'armed';
    });

    const alarmInputLabel = computed(() => {
      if (!alarmInputFiable.value) return 'Inconnu';
      return device.value.alarm_input_state ? 'Actif' : 'Au repos';
    });
    const alarmInputBadge = computed(() => {
      if (!alarmInputFiable.value) return 'bg-gray-100 text-gray-500';
      return device.value.alarm_input_state ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700';
    });

    // Pourquoi on ne sait pas, et ce qu'on savait en dernier — affiché sous le badge.
    const alarmInputNote = computed(() => {
      const d = device.value;
      if (!d || alarmInputFiable.value) return null;
      if (d.alarm_input_state === null || d.alarm_input_state === undefined) {
        return 'Aucune transition reçue depuis le dernier démarrage de la carte.';
      }
      return `Remontée d'état coupée : le niveau n'est plus rafraîchi. Dernière valeur connue : `
        + `${d.alarm_input_state ? 'actif' : 'au repos'}.`;
    });

    // Rôle de l'entrée filaire — calculé par le SERVEUR (`wired_role`), pas ici : la règle
    // « ce que la carte rapporte prime sur ce qu'on lui a demandé » lui appartient, et deux
    // interfaces qui la reconstruiraient finiraient par ne plus dire la même chose. Le repli
    // n'existe que pour une fiche chargée avant la migration.
    const wiredRole = computed(() => (device.value && device.value.wired_role)
      || { h24: false, siren: true, nature: 'intrusion', report: false, warn_no_report: false });

    const wiredRoleLabel = computed(() => {
      switch (wiredRole.value.nature) {
        case 'technique_sonore':      return 'Alarme technique 24h/24 (sirène)';
        case 'technique_silencieuse': return 'Alarme technique 24h/24 (silencieuse)';
        default:                      return 'Zone d\'intrusion (système armé)';
      }
    });

    // Défaut capteur : la carte a suspendu la surveillance. Persistant par construction — le
    // drapeau vient de la base et du push temps réel, pas d'un événement fugace.
    const wiredFault = computed(() => !!(device.value && device.value.wired_fault));

    // Armement sur zone ouverte : l'avertissement ne vaut que pendant l'armement, et le serveur
    // efface l'horodatage au désarmement comme au boot. On ne le réaffiche donc pas de nous-mêmes.
    const armZoneOpen = computed(() => !!(device.value && device.value.arm_zone_open_at));
    const armZoneOpenOrigine = computed(() => {
      const src = device.value && device.value.arm_zone_open_src;
      return src === 'schedule' ? 'le planning automatique'
           : src === 'rf'       ? 'une télécommande RF'
           : src === 'mqtt'     ? 'une commande à distance'
           : 'une origine inconnue';
    });

    const getDevType = (type) => {
      const types = { 1: 'Télécommande RF', 18: 'PIR', 19: 'Ouverture', 47: 'Choc/Vibration', 49: 'Panneau' };
      return types[type] || `Type ${type}`;
    };

    const formatDate = (d) => {
      if (!d) return 'N/A';
      try { return format(enParis(parseISO(d)), "dd MMM yyyy HH:mm", { locale: fr }); }
      catch { return d; }
    };

    onMounted(async () => {
      try {
        const result = await store.dispatch('device/fetchDetails', deviceId);
        device.value      = result.device;
        accessories.value = result.accessories || [];
        contacts.value    = result.contacts || [];
        events.value      = result.events || [];
        if (result.device?.last_lat && result.device?.last_lon) {
          lastGnssPosition.value = {
            lat:       result.device.last_lat,
            lon:       result.device.last_lon,
            fix:       result.device.last_gnss_fix,
            sat:       result.device.last_gnss_sat,
            src:       result.device.last_gnss_src,
            timestamp: result.device.last_gnss_at
          };
        }
        // Réactive le badge maintenance si la fenêtre est encore ouverte côté serveur
        // (cas reload page / navigation interne / push Socket.io raté).
        reconcileMaintWindow();
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    });

    // ---- Fenêtre de maintenance (batterie) ----
    const maintWindowOpen = ref(false);
    const injoignable = computed(() => carteEndormie(device.value) && !maintWindowOpen.value);
    const maintCountdown = ref(0);
    let maintTimer = null;

    const startMaintCountdown = (seconds) => {
      maintWindowOpen.value = true;
      maintCountdown.value = seconds;
      if (maintTimer) clearInterval(maintTimer);
      maintTimer = setInterval(() => {
        maintCountdown.value--;
        if (maintCountdown.value <= 0) {
          maintWindowOpen.value = false;
          clearInterval(maintTimer);
          maintTimer = null;
        }
      }, 1000);
    };

    const stopMaintCountdown = () => {
      maintWindowOpen.value = false;
      maintCountdown.value = 0;
      if (maintTimer) { clearInterval(maintTimer); maintTimer = null; }
    };

    // Reconcile : relit `device.window_open_until` (persisté en BDD côté serveur)
    // et active/désactive le badge selon le temps restant. Indispensable pour
    // survivre à un reload de page, un changement d'onglet, ou un push Socket.io raté.
    const reconcileMaintWindow = () => {
      const d = device.value;
      if (!d || !d.window_open_until) {
        stopMaintCountdown();
        return;
      }
      const remainingMs = new Date(d.window_open_until).getTime() - Date.now();
      if (remainingMs <= 0) {
        stopMaintCountdown();
      } else {
        startMaintCountdown(Math.ceil(remainingMs / 1000));
      }
    };

    // ---- Modifier device ----
    const editDeviceModal = ref(false);
    const editDevice = ref({ name: '', location: '' });

    const openEditDevice = () => {
      editDevice.value = { name: device.value.name, location: device.value.location };
      editDeviceModal.value = true;
    };

    const saveDevice = async () => {
      try {
        await store.dispatch('device/updateDevice', { deviceId, deviceData: editDevice.value });
        Object.assign(device.value, editDevice.value);
        editDeviceModal.value = false;
      } catch (e) {
        console.error(e);
      }
    };

    const confirmDeleteDevice = async () => {
      if (!confirm(`Supprimer le dispositif "${device.value.name}" ?`)) return;
      try {
        await store.dispatch('device/deleteDevice', deviceId);
        router.push(`/admin/clients/${device.value.client_id}`);
      } catch (e) {
        console.error(e);
      }
    };

    // Depuis la v1.3.52 (fw_ver 66356), un set_config qui change RÉELLEMENT le drapeau rf
    // déclenche un reboot différé côté carte. Avant, le drapeau était persisté mais jamais
    // appliqué : c'est l'opérateur qui devait redémarrer. La consigne affichée dépend donc du
    // firmware embarqué, pas d'une préférence — d'où ce seuil en dur.
    const RF_AUTO_REBOOT_FW = 66356;
    const rfRebootsItself = computed(() =>
      !!(device.value && device.value.fw_ver != null && device.value.fw_ver >= RF_AUTO_REBOOT_FW));

    // ---- Config désirée vs rapportée ----
    // Le serveur livre l'écart déjà calculé (colonnes du device = désiré, `reported_config` =
    // ce que la carte dit appliquer) : le frontend l'affiche, il ne le recalcule pas.
    const configDivergence = computed(() => (device.value && device.value.config_divergence) || []);

    const rfReported = computed(() => {
      const r = device.value && device.value.reported_config;
      return r && r.rf !== undefined ? !!r.rf : null;   // null = la carte ne l'a jamais rapporté
    });

    const jauge = computed(() => libelleJauge(device.value && device.value.jauge));

    const reportedConfigAt = computed(() => {
      const at = device.value && device.value.reported_config_at;
      return at ? formatDate(at) : 'jamais';
    });

    function fmtCfgValue(v) {
      if (v === true)  return 'activé';
      if (v === false) return 'désactivé';
      return String(v);
    }

    // ---- Commandes ----
    const cmdLoading = ref(false);
    const cmdFeedback = ref(null);

    const accLoading = ref(false);
    const reloadAccessories = async () => {
      accLoading.value = true;
      try {
        const res = await apiService.getDeviceAccessories(deviceId);
        accessories.value = res.data;
      } catch (e) {
        console.error('Erreur rechargement accessoires:', e);
      } finally {
        accLoading.value = false;
      }
    };

    const reloadDevice = async () => {
      try {
        const result = await store.dispatch('device/fetchDetails', deviceId);
        device.value = result.device;
        accessories.value = result.accessories || [];
        contacts.value = result.contacts || [];
        events.value = result.events || [];
      } catch (e) {
        console.error('Erreur rechargement device:', e);
      }
    };

    const sendCmdAndReload = async (command, data) => {
      await sendCmd(command, data);
      setTimeout(reloadDevice, 3000);
    };

    // Renvoie `true` si le serveur a accepté la commande : les encadrés partagés en ont besoin
    // (le jumelage ne lance son décompte qu'à cette condition).
    const sendCmd = async (command, data) => {
      cmdLoading.value = true;
      cmdFeedback.value = null;
      try {
        const res = await store.dispatch('device/sendCommand', { deviceId, command, data });
        // Le jumelage a son propre retour (décompte puis résultat) dans son encadré.
        if (command !== 'pair_start') {
          // Le serveur sait CE QU'IL FAUT ATTENDRE (fenêtre PANIC pour un reboot, prochaine
          // connexion programmée pour un drapeau de config) : on affiche son message tel quel
          // plutôt qu'un texte générique qui laisserait l'opérateur sans consigne.
          cmdFeedback.value = {
            success: true,
            message: (res && res.message) || `Commande "${command}" envoyée avec succès.`
          };
          if (command === 'list_accessories') {
            cmdFeedback.value.message += ' Interrogation en cours...';
            setTimeout(reloadAccessories, 3000);
          }
        }
        return true;
      } catch (e) {
        cmdFeedback.value = { success: false, message: e.response?.data?.message || `Erreur lors de l'envoi de "${command}".` };
        return false;
      } finally {
        cmdLoading.value = false;
        setTimeout(() => { cmdFeedback.value = null; }, 5000);
      }
    };

    // ---- Socket.io temps réel ----
    const socket = socketService.connect();

    socket.on('device:update', (data) => {
      if (data.deviceId !== deviceId || !device.value) return;
      Object.assign(device.value, data.fields);
      device.value.last_seen = new Date().toISOString();
      // Si le push contient window_open_until, recalibrer le badge maintenance
      if (Object.prototype.hasOwnProperty.call(data.fields || {}, 'window_open_until')) {
        reconcileMaintWindow();
      }
    });

    socket.on('device:event', (data) => {
      if (data.deviceId !== deviceId) return;
      events.value.unshift({
        event_type: data.eventType,
        event_message: data.message,
        timestamp: new Date().toISOString()
      });
      if (events.value.length > 20) events.value.pop();
    });

    socket.on('device:window', (data) => {
      if (data.deviceId !== deviceId) return;
      if (data.open) {
        startMaintCountdown(data.duration_s || 120);
      } else {
        stopMaintCountdown();
      }
    });

    const onAccessories = (data) => {
      if (data.deviceId !== deviceId) return;
      reloadAccessories();
    };
    socket.on('device:accessories', onAccessories);

    socket.on('device:gnss', (data) => {
      if (data.deviceId !== deviceId) return;
      lastGnssPosition.value = {
        lat:       data.lat,
        lon:       data.lon,
        fix:       data.fix,
        sat:       data.sat,
        src:       data.src,
        timestamp: data.timestamp
      };
    });

    onBeforeUnmount(() => {
      socket.off('device:update');
      socket.off('device:event');
      socket.off('device:window');
      socket.off('device:accessories', onAccessories);
      socket.off('device:gnss');
      if (maintTimer) clearInterval(maintTimer);
    });

    return {
      loading, device, accessories, contacts, events,
      deviceId,
      activeTab, tabs,
      stateBadge, stateLabel, powerBadge, powerLabel,
      alarmInputLabel, alarmInputBadge, alarmInputNote,
      wiredRole, wiredRoleLabel, wiredFault, armZoneOpen, armZoneOpenOrigine,
      maintWindowOpen, maintCountdown, injoignable, messageEndormie,
      getDevType, formatDate, fwLabel: versionLabel,
      editDeviceModal, editDevice, openEditDevice, saveDevice, confirmDeleteDevice,
      reloadAccessories, accLoading, sendCmdAndReload,
      cmdLoading, cmdFeedback, sendCmd,
      wsConnected: socketService.connected,
      lastGnssPosition,
      configDivergence, rfReported, reportedConfigAt, fmtCfgValue, jauge,
      rfRebootsItself
    };
  }
});
</script>
